/**
 * The prediction board's lens, kept in the URL.
 *
 * The Actors route promises a board that "deep-links and survives the round
 * trip to a wallet's page". This board kept its sort, its minimum, how many
 * wallets it read and which wallet was open in component state, so opening a
 * wallet and pressing Back put a reader ranking by P&L back on the default
 * ranking, one row expanded fewer and a hundred wallets short.
 *
 * A non-component module: the router parses the search without loading the
 * page, and Vite's Fast Refresh rejects a component module that exports
 * anything else.
 */
import {
  DEFAULT_PREDICTION_LIMIT,
  type PredictionBoardOrder,
  type PredictionSort,
  type PredictionWallet,
} from "@authe/fe-commons/prediction";
import { normalizeAddress } from "@authe/fe-commons/chains";

export const PREDICTION_SORT_IDS = [
  "confidence",
  "rate",
  "informed",
  "pnl",
  "markets",
] as const satisfies readonly PredictionSort[];
export const DEFAULT_PREDICTION_SORT: PredictionSort = "confidence";

export const MIN_MARKETS_OPTIONS = [1, 3, 5, 10, 25] as const;
export const DEFAULT_MIN_MARKETS = 3;

/** More than any view read returns (the API clamps); a pasted `n=1e9` stops here. */
const MAX_READ = 10_000;

export interface PredictionBoardSearch {
  sort?: PredictionSort;
  /** Fewest settled markets a wallet needs to be listed. */
  min?: number;
  /** Wallets read from the warehouse, when more than the default. */
  n?: number;
  /** The wallet whose settled markets are open under its row. */
  wallet?: string;
}

/**
 * The route's `validateSearch`. Defaults leave the URL, so the plain board is
 * a plain link, and anything unrecognised is dropped rather than breaking the
 * page.
 */
export function parsePredictionBoardSearch(search: Record<string, unknown>): PredictionBoardSearch {
  const sort = PREDICTION_SORT_IDS.find((id) => id === search.sort);
  const min = Number(search.min);
  const n = Number(search.n);
  const wallet = typeof search.wallet === "string" ? search.wallet.trim() : "";
  return {
    sort: sort && sort !== DEFAULT_PREDICTION_SORT ? sort : undefined,
    min: (MIN_MARKETS_OPTIONS as readonly number[]).includes(min) && min !== DEFAULT_MIN_MARKETS ? min : undefined,
    n: Number.isInteger(n) && n > DEFAULT_PREDICTION_LIMIT ? Math.min(n, MAX_READ) : undefined,
    wallet: wallet ? normalizeAddress(wallet) : undefined,
  };
}

/** What the warehouse ordered the read by, in the words the caption uses. */
export const SERVER_ORDER_NOUN: Record<PredictionBoardOrder, string> = {
  won: "markets won",
  pnl: "P&L",
  informed: "informed entries",
  markets: "markets settled",
};

/**
 * Whether the read stopped at a limit, and whether asking for more could help.
 *
 * `has_more` is the answer: the handler fetches one row past the limit to
 * know. Inferring it from `items.length >= limit` fails both ways at the
 * boundary — and past the API's clamp (500) it said a 500-row read of an
 * 800-row request was the whole board, and took the "read more" away.
 */
export function readCut(
  page: { items?: readonly unknown[]; has_more?: boolean; limit?: number } | undefined,
  requested: number,
): { fetched: number; truncated: boolean; canReadMore: boolean } {
  const fetched = page?.items?.length ?? 0;
  const truncated = page?.has_more ?? fetched >= requested;
  // A clamp means the next request would come back the same size.
  const clamped = page?.limit !== undefined && page.limit < requested;
  return { fetched, truncated, canReadMore: truncated && !clamped };
}

/**
 * Why a ranking by informed entries has no order to show, when it has none.
 *
 * Every wallet at zero is the common case in sandbox (the $100 floor binds —
 * see `INFORMED_EXPLAINER`), and a table headed "ranked by informed entries"
 * whose column is all zeros reads as a ranking. It is only the tie-break.
 */
export function informedTieNote(
  sort: PredictionSort,
  wallets: readonly PredictionWallet[],
): string | null {
  if (sort !== "informed" || wallets.length === 0) return null;
  if (wallets.some((w) => w.informed > 0)) return null;
  return `None of these ${wallets.length} wallets has an informed entry, so this order is only the tie-break: most markets settled first.`;
}

/**
 * Why a minimum emptied the board — and whether that is the whole answer.
 *
 * The read is a top-N by `order`, so when it was cut a wallet below the cut can
 * still meet the minimum, unless the read was ordered by markets settled itself:
 * then the best-placed wallet failing it means every wallet does. The old copy
 * ("the 200 wallets read have fewer…") was true and read as "none exist".
 */
export function minimumEmptyNote({
  order,
  truncated,
  minMarkets,
  mostSettled,
  fetched,
}: {
  order: PredictionBoardOrder;
  truncated: boolean;
  minMarkets: number;
  /** The most markets any wallet read has settled. */
  mostSettled: number;
  fetched: number;
}): { text: string; rankBySettled: boolean } {
  if (!truncated || order === "markets") {
    return {
      text: `No wallet has settled ${minMarkets} markets yet; the most any has is ${mostSettled}. Lower the minimum to see them.`,
      rankBySettled: false,
    };
  }
  return {
    text: `The top ${fetched} wallets by ${SERVER_ORDER_NOUN[order]} each have fewer than ${minMarkets} settled markets, but a wallet further down could have more. Ranking by markets settled checks the wallets with the most.`,
    rankBySettled: true,
  };
}
